/* eslint-disable react/no-children-prop */
import React, { useState, useRef, useEffect } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ReactDOMServer from 'react-dom/server'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-toward.css'

import RoundedIcon from '~components/ui/RoundedIcon'
import MediaQuery from '~components/helpers/MediaQuery'
import { isScreenSize } from '~helpers/mediaQuery'
import Sprite from '~components/Sprite'
import Logout from '~components/helpers/Logout'
import { getAvailableCredit, getCreditLimit } from '~helpers/price'

const AccountMenu = ({ stockist }) => {
  const [logout, setLogout] = useState(false)
  const [active, setActive] = useState(false)
  const navLinkRef = useRef(null)
  const customerNumber = stockist.customer_number.replace('{[BS]}', '')
  let tippyInstance = null

  const items = [
    {
      title: 'Stockist Code',
      value: stockist?.customer_number
    },
    {
      title: 'Stockist Name',
      value: stockist?.name
    },
    {
      title: 'Drop Seq',
      value: stockist?.sequence_number
    },
    {
      title: 'Credit Limit',
      value: stockist?.credit?.creditLimit
    }
  ]

  useEffect(() => {
    initTooltip()
    const logoutHandler = (e) => {
      if (e.target && e.target.matches('[data-logout]')) {
        setLogout(true)
      }
    }

    document.addEventListener('click', logoutHandler)

    return () => {
      document.removeEventListener('click', logoutHandler)
      tippyInstance && tippyInstance.destroy()
    }
  }, [])

  const initTooltip = () => {
    tippyInstance = tippy(navLinkRef.current, {
      arrow: true,
      interactive: true,
      allowHTML: true,
      theme: 'pink',
      animation: 'shift-toward',
      offset: [0, 16],
      maxWidth: 'none',
      content: getTooltipContent(),
      onShow: () => setActive(true),
      onHide: () => setActive(false)
    })
  }

  const getTooltipContent = () => {
    if (!items) return null

    return ReactDOMServer.renderToString(
      <div className="relative">
        <div className="w-auto">
          <ul className="-mb-2 min-w-90">
            {items.map((item, index) => {
              if (item.title === 'Credit Limit') {
                return (
                  <li key={index} className="mb-2">
                    {`${item.title}: `}
                    <strong>
                      {getCreditLimit(stockist)}
                    </strong>
                  </li>
                )
              } else if (item.title === 'Stockist Code') {
                return (
                  <li key={index} className="mb-2">
                    {`${item.title}: `}
                    <strong>
                      {item.value.replace('{[BS]}', '')}
                    </strong>
                  </li>
                )
              } else {
                return (
                  <li key={index} className="mb-2">
                    {`${item.title}: `}
                    <strong>
                      {item.value}
                    </strong>
                  </li>
                )
              }
            })}
          </ul>
        </div>

        <div className="pt-4 mt-6 -mb-2 border-t border-charcoal">
          <a
            href="/account"
            className="flex items-center mb-2 hover:underline focus:underline"
          >
            View Account
          </a>

          <button
            type="button"
            className="flex items-center mb-2 hover:underline focus:underline"
            data-logout
          >
            Log out

            <span
              className="block w-5 h-5 ml-2 icon icon-fill"
              aria-hidden
            >
              <Sprite name="exit" />
            </span>
          </button>
        </div>
      </div>
    )
  }

  const match = useMatch('/account')
  const isActive = !!match

  return (
    <>
      {logout && <Logout />}

      <NavLink
        to="/account"
        className="flex items-center group"
        ref={navLinkRef}
        onClick={(e) => !isScreenSize('lg') && e.preventDefault()}
      >
        <RoundedIcon
          name="user"
          className="block w-10 h-10 lg:w-7 lg:h-7"
          active={isActive || active}
        />

        <MediaQuery breakpoint="xl">
          <span className="block w-auto ml-2">
            <span className="block text-base">
              {`${stockist.name} ${customerNumber} ${stockist.sequence_number}`}
            </span>

            <span className="block text-xxs">
              {'Available Credit: '}

              <strong>
                {getAvailableCredit(stockist)}
              </strong>
            </span>
          </span>
        </MediaQuery>
      </NavLink>
    </>
  )
}

AccountMenu.propTypes = {
  stockist: PropTypes.object
}

const mapStateToProps = (state) => ({
  stockist: state.auth.account.stockist
})

export default connect(mapStateToProps)(AccountMenu)
